import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';
import { BORDER_RADIUS } from '@/constants/config';
import { snippetExcludeWebProps } from '@/utils/topic_seo';

export default function TopicPageFeedback({ accent, onPress }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();

  const styles = useMemo(() => StyleSheet.create({
    card: {
      flexDirection: isXS ? 'column' : 'row',
      alignItems: isXS ? 'stretch' : 'center',
      gap: isXS ? 14 : 16,
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: accent ? `${accent}44` : colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      padding: isXS ? 16 : 18,
    },
    copy: {
      flex: 1,
      gap: 6,
      minWidth: 0,
    },
    eyebrow: {
      fontSize: 11,
      fontWeight: '800',
      letterSpacing: 1.1,
      opacity: 0.65,
      textTransform: 'uppercase',
    },
    body: {
      opacity: 0.88,
      lineHeight: 22,
    },
    iconWrap: {
      width: 32,
      height: 32,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: accent ? `${accent}18` : colors.solid_color_tertiary,
      flexShrink: 0,
    },
    action: {
      flexShrink: 0,
      alignSelf: isXS ? 'stretch' : 'center',
    },
  }), [colors, isXS, accent]);

  const iconColor = accent || colors.text_color_secondary;

  return (
    <View style={styles.card} {...snippetExcludeWebProps()}>
      <View style={styles.iconWrap}>
        <Icon name="comment-dots" size={15} color={iconColor} />
      </View>
      <View style={styles.copy}>
        <Text style={styles.eyebrow}>{t('pages.topics.feedbackEyebrow')}</Text>
        <Text type="p" size="s" style={styles.body}>
          {t('pages.topics.feedbackSectionBody')}
        </Text>
      </View>
      <View style={styles.action}>
        <Button
          variant="secondary"
          size={isXS ? 'medium' : 'small'}
          text={t('pages.topics.feedbackButton')}
          prependIcon="comment-dots"
          onPress={onPress}
          isFullWidth={isXS}
        />
      </View>
    </View>
  );
}
